import React, { useState, useEffect } from 'react';
import { Button, Select, SpaceBetween, Box, Header } from '@cloudscape-design/components';
import { themes, Theme, applyTheme, getCurrentTheme } from '../../themes/themeConfig';
import './ThemeSelector.css';

interface ThemeSelectorProps {
  onThemeChange?: (theme: Theme) => void;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ onThemeChange }) => {
  const [selectedTheme, setSelectedTheme] = useState<Theme>(getCurrentTheme());

  useEffect(() => {
    // Apply the current theme on component mount
    applyTheme(selectedTheme);
  }, []);

  const handleThemeChange = (theme: Theme) => {
    setSelectedTheme(theme);
    applyTheme(theme);
    onThemeChange?.(theme);
  };

  const themeOptions = themes.map(theme => ({
    label: theme.displayName,
    value: theme.name,
    description: theme.description
  }));

  return (
    <div className="theme-selector-panel">
      <Box padding="m">
        <SpaceBetween direction="vertical" size="m">
          <Header variant="h3">Theme Settings</Header>
          
          <Select
            selectedOption={{
              label: selectedTheme.displayName,
              value: selectedTheme.name,
              description: selectedTheme.description
            }}
            onChange={({ detail }) => {
              const theme = themes.find(t => t.name === detail.selectedOption.value);
              if (theme) {
                handleThemeChange(theme);
              }
            }}
            options={themeOptions}
            placeholder="Choose a theme"
          />
          
          <div className="theme-preview-grid">
            {themes.map(theme => (
              <div
                key={theme.name}
                className={`theme-preview-card ${selectedTheme.name === theme.name ? 'selected' : ''}`}
                onClick={() => handleThemeChange(theme)}
              >
                <div className="theme-preview-colors">
                  <div 
                    className="color-swatch primary" 
                    style={{ backgroundColor: theme.colors.primary }}
                  />
                  <div 
                    className="color-swatch secondary" 
                    style={{ backgroundColor: theme.colors.secondary }}
                  />
                  <div 
                    className="color-swatch accent" 
                    style={{ backgroundColor: theme.colors.accent1 }}
                  />
                </div>
                <div className="theme-preview-info">
                  <div className="theme-name">{theme.displayName}</div>
                  <div className="theme-description">{theme.description}</div>
                </div>
              </div>
            ))}
          </div>
          
          <Button
            variant="link"
            onClick={() => onThemeChange?.(selectedTheme)}
          >
            Close
          </Button>
        </SpaceBetween>
      </Box>
    </div>
  );
};

export default ThemeSelector;
